import {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'motion/react';
import { Check, ChevronDown, CircleAlert, Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  AsYouType,
  getCountries,
  getCountryCallingCode,
  isValidPhoneNumber,
  parsePhoneNumberFromString,
  type CountryCode,
} from 'libphonenumber-js';
import { cn } from './cn';

export type PhoneInputSize = 'sm' | 'md' | 'lg';

export interface PhoneInputProps {
  /** Normalized E.164 value (e.g. "+421901234567"), or "" when empty. */
  value: string;
  /** Emits the normalized E.164 string ("" when the national part is empty). */
  onChange: (value: string) => void;
  label?: React.ReactNode;
  /** Error message (string) or error flag (boolean). Renders the danger (red) validation treatment. */
  error?: string | boolean;
  helperText?: React.ReactNode;
  required?: boolean;
  disabled?: boolean;
  /** Placeholder for the national-number field. */
  placeholder?: string;
  /** Country selected by default when the value carries no country. Default 'SK'. */
  defaultCountry?: CountryCode;
  name?: string;
  id?: string;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  inputSize?: PhoneInputSize;
  fullWidth?: boolean;
  containerClassName?: string;
  autoComplete?: string;
}

/** True when `value` is a valid E.164 phone number. Empty/invalid → false. */
export function isValidPhone(value?: string | null): boolean {
  if (!value) return false;
  try {
    return isValidPhoneNumber(value);
  } catch {
    return false;
  }
}

const fieldSizes: Record<PhoneInputSize, string> = {
  sm: 'h-9 text-sm',
  md: 'h-11 text-sm',
  lg: 'h-12 text-base',
};

const MENU_MAX_HEIGHT = 320;
const MENU_GAP = 6;

interface MenuPosition {
  left: number;
  width: number;
  top?: number;
  bottom?: number;
  placement: 'top' | 'bottom';
}

interface CountryEntry {
  code: CountryCode;
  callingCode: string;
  name: string;
  flag: string;
}

/** flagcdn SVG (matches the rest of the app); renders cross-platform. */
const flagUrl = (code: string) => `https://flagcdn.com/${code.toLowerCase()}.svg`;

/**
 * Branded international phone field — ONE shared control for every phone input.
 * A searchable country selector (flag + dial code, default Slovakia) sits next to
 * a national-number field; the emitted value is always normalized E.164. The
 * country menu is portaled to <body> (like <Select>) so it is never clipped inside
 * a dialog. Mirrors <Input> chrome incl. the danger (red) field-validation error state.
 */
export const PhoneInput = forwardRef<HTMLInputElement, PhoneInputProps>(
  (
    {
      value,
      onChange,
      label,
      error,
      helperText,
      required,
      disabled,
      placeholder,
      defaultCountry = 'SK',
      name,
      id,
      onBlur,
      inputSize = 'md',
      fullWidth = true,
      containerClassName,
      autoComplete = 'tel',
    },
    ref,
  ) => {
    const { t, i18n } = useTranslation();
    const reactId = useId();
    const fieldId = id ?? reactId;
    const listboxId = `${fieldId}-country-list`;
    const hasError = Boolean(error);
    const errorText = typeof error === 'string' ? error : undefined;

    const [country, setCountry] = useState<CountryCode>(defaultCountry);
    const [national, setNational] = useState('');
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [activeIndex, setActiveIndex] = useState(0);
    const [menuPos, setMenuPos] = useState<MenuPosition | null>(null);

    const rootRef = useRef<HTMLDivElement>(null);
    const fieldRef = useRef<HTMLDivElement>(null);
    const countryBtnRef = useRef<HTMLButtonElement>(null);
    const nationalRef = useRef<HTMLInputElement>(null);
    const searchRef = useRef<HTMLInputElement>(null);
    const listRef = useRef<HTMLUListElement>(null);
    const optionRefs = useRef<Array<HTMLLIElement | null>>([]);
    const lastEmitted = useRef<string | null>(null);
    const countryRef = useRef<CountryCode>(defaultCountry);
    countryRef.current = country;

    useImperativeHandle(ref, () => nationalRef.current as HTMLInputElement, []);

    // ── Country metadata (localized names, dial codes, flags) ──
    const regionNames = useMemo(() => {
      try {
        return new Intl.DisplayNames([i18n.language], { type: 'region' });
      } catch {
        return null;
      }
    }, [i18n.language]);

    const countries = useMemo<CountryEntry[]>(() => {
      return getCountries()
        .map((code) => {
          let displayName = code as string;
          try {
            displayName = regionNames?.of(code) ?? code;
          } catch {
            /* keep ISO code */
          }
          return {
            code,
            callingCode: getCountryCallingCode(code),
            name: displayName,
            flag: flagUrl(code),
          };
        })
        .sort((a, b) => a.name.localeCompare(b.name, i18n.language));
    }, [regionNames, i18n.language]);

    const selected = useMemo(
      () => countries.find((c) => c.code === country),
      [countries, country],
    );

    const filtered = useMemo(() => {
      const q = search.trim().toLowerCase();
      if (!q) return countries;
      const digits = q.replace(/\D/g, '');
      return countries.filter(
        (c) =>
          c.name.toLowerCase().includes(q) ||
          c.code.toLowerCase().includes(q) ||
          `+${c.callingCode}`.includes(q) ||
          (digits.length > 0 && c.callingCode.includes(digits)),
      );
    }, [countries, search]);

    // ── Controlled value sync (parse incoming E.164 → country + national) ──
    useEffect(() => {
      if (value === lastEmitted.current) return;
      if (!value) {
        setNational('');
        lastEmitted.current = '';
        return;
      }
      const parsed = parsePhoneNumberFromString(value.startsWith('+') ? value : `+${value}`);
      if (parsed?.country) {
        setCountry(parsed.country);
        setNational(new AsYouType(parsed.country).input(parsed.nationalNumber));
      } else if (parsed) {
        setNational(new AsYouType(countryRef.current).input(parsed.nationalNumber));
      } else {
        const digits = value.replace(/\D/g, '');
        setNational(new AsYouType(countryRef.current).input(digits));
      }
      lastEmitted.current = value;
      // Only resync when the external value actually changes.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    const emit = useCallback(
      (c: CountryCode, nat: string) => {
        const digits = nat.replace(/\D/g, '');
        const e164 = digits ? `+${getCountryCallingCode(c)}${digits}` : '';
        lastEmitted.current = e164;
        onChange(e164);
      },
      [onChange],
    );

    const handleNationalChange = (raw: string) => {
      const formatted = new AsYouType(country).input(raw.replace(/[^\d+\s()-]/g, ''));
      setNational(formatted);
      emit(country, formatted);
    };

    const closeMenu = useCallback((focusBtn = true) => {
      setOpen(false);
      setSearch('');
      if (focusBtn) countryBtnRef.current?.focus();
    }, []);

    const selectCountry = useCallback(
      (c: CountryCode) => {
        setCountry(c);
        emit(c, national);
        setOpen(false);
        setSearch('');
        requestAnimationFrame(() => nationalRef.current?.focus());
      },
      [emit, national],
    );

    // ── Portaled menu positioning (mirrors <Select>) ──
    const updatePosition = useCallback(() => {
      const el = fieldRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const flipUp = spaceBelow < Math.min(MENU_MAX_HEIGHT, 260) && spaceAbove > spaceBelow;
      setMenuPos(
        flipUp
          ? { placement: 'top', left: rect.left, width: rect.width, bottom: window.innerHeight - rect.top + MENU_GAP }
          : { placement: 'bottom', left: rect.left, width: rect.width, top: rect.bottom + MENU_GAP },
      );
    }, []);

    useLayoutEffect(() => {
      if (!open) return;
      updatePosition();
      setActiveIndex(0);
      const onScroll = (e: Event) => {
        if (listRef.current?.contains(e.target as Node)) return;
        updatePosition();
      };
      window.addEventListener('scroll', onScroll, true);
      window.addEventListener('resize', updatePosition);
      const focusTimer = window.setTimeout(() => searchRef.current?.focus(), 10);
      return () => {
        window.removeEventListener('scroll', onScroll, true);
        window.removeEventListener('resize', updatePosition);
        window.clearTimeout(focusTimer);
      };
    }, [open, updatePosition]);

    // Reset highlight to top whenever the filtered set changes.
    useEffect(() => {
      setActiveIndex(0);
    }, [search]);

    useEffect(() => {
      if (!open || activeIndex < 0) return;
      optionRefs.current[activeIndex]?.scrollIntoView({ block: 'nearest' });
    }, [open, activeIndex]);

    // Outside-click closes the menu (menu is portaled, so check both refs).
    useEffect(() => {
      if (!open) return;
      const handlePointer = (e: MouseEvent | TouchEvent) => {
        const target = e.target as Node;
        if (!rootRef.current?.contains(target) && !listRef.current?.contains(target)) {
          setOpen(false);
          setSearch('');
        }
      };
      document.addEventListener('mousedown', handlePointer);
      document.addEventListener('touchstart', handlePointer);
      return () => {
        document.removeEventListener('mousedown', handlePointer);
        document.removeEventListener('touchstart', handlePointer);
      };
    }, [open]);

    const onSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setActiveIndex((i) => Math.min(i + 1, filtered.length - 1));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setActiveIndex((i) => Math.max(i - 1, 0));
          break;
        case 'Enter': {
          e.preventDefault();
          const pick = filtered[activeIndex];
          if (pick) selectCountry(pick.code);
          break;
        }
        case 'Escape':
          e.preventDefault();
          e.stopPropagation();
          closeMenu();
          break;
        default:
          break;
      }
    };

    return (
      <div
        ref={rootRef}
        className={cn('flex flex-col gap-1.5', fullWidth && 'w-full', containerClassName)}
      >
        {label && (
          <label htmlFor={`${fieldId}-national`} className="text-sm font-medium text-content">
            {label}
            {required && <span className="ml-0.5 text-primary">*</span>}
          </label>
        )}

        {name && <input type="hidden" name={name} value={value} />}

        <div
          ref={fieldRef}
          className={cn(
            'group flex items-center rounded-xl border bg-surface pr-3.5 transition-all duration-200',
            // Field-validation errors use the danger (red) token + icon (CONVENTIONS §5).
            hasError
              ? 'border-danger ring-2 ring-danger/25 focus-within:border-danger focus-within:ring-danger/40'
              : 'border-line hover:border-primary/40 focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/30',
            disabled && 'opacity-60 pointer-events-none',
            fieldSizes[inputSize],
          )}
        >
          <button
            ref={countryBtnRef}
            type="button"
            disabled={disabled}
            onClick={() => (open ? closeMenu() : setOpen(true))}
            aria-haspopup="listbox"
            aria-expanded={open}
            aria-controls={open ? listboxId : undefined}
            aria-label={t('common.phone.select_country', 'Select country code')}
            className="flex h-full shrink-0 items-center gap-1.5 rounded-l-xl pl-3.5 pr-2.5 text-content outline-none transition-colors hover:bg-primary/5 focus-visible:bg-primary/5"
          >
            {selected && (
              <img
                src={selected.flag}
                alt=""
                width={20}
                height={15}
                loading="lazy"
                className="h-[15px] w-5 rounded-[3px] border border-line/70 object-cover"
              />
            )}
            <span className="text-sm font-medium tabular-nums text-content">
              +{selected?.callingCode ?? getCountryCallingCode(country)}
            </span>
            <ChevronDown
              size={16}
              aria-hidden
              className={cn('text-muted transition-transform duration-200', open && 'rotate-180 text-primary')}
            />
          </button>

          <span aria-hidden className="mx-1 h-5 w-px shrink-0 bg-line" />

          <input
            ref={nationalRef}
            id={`${fieldId}-national`}
            type="tel"
            inputMode="tel"
            autoComplete={autoComplete}
            disabled={disabled}
            required={required}
            aria-invalid={hasError || undefined}
            placeholder={placeholder ?? '901 234 567'}
            value={national}
            onChange={(e) => handleNationalChange(e.target.value)}
            onBlur={onBlur}
            className="h-full min-w-0 flex-1 bg-transparent pl-1.5 text-content placeholder:text-muted/70 outline-none"
          />

          {hasError && (
            <span className="ml-1 shrink-0 text-danger" aria-hidden="true">
              <CircleAlert size={18} strokeWidth={2} />
            </span>
          )}
        </div>

        {errorText ? (
          <p className="flex items-start gap-1.5 text-xs font-medium text-danger">
            <CircleAlert size={13} strokeWidth={2.5} className="mt-px shrink-0" aria-hidden="true" />
            <span>{errorText}</span>
          </p>
        ) : helperText ? (
          <p className="text-xs text-muted">{helperText}</p>
        ) : null}

        {typeof document !== 'undefined' &&
          createPortal(
            <AnimatePresence>
              {open && menuPos && (
                <motion.ul
                  ref={listRef}
                  id={listboxId}
                  role="listbox"
                  tabIndex={-1}
                  initial={{ opacity: 0, y: menuPos.placement === 'top' ? 4 : -4, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: menuPos.placement === 'top' ? 4 : -4, scale: 0.98 }}
                  transition={{ duration: 0.15, ease: 'easeOut' }}
                  style={{
                    position: 'fixed',
                    left: menuPos.left,
                    width: menuPos.width,
                    top: menuPos.top,
                    bottom: menuPos.bottom,
                    zIndex: 1400,
                    transformOrigin: menuPos.placement === 'top' ? 'bottom' : 'top',
                  } as CSSProperties}
                  className="flex max-h-80 flex-col overflow-hidden rounded-xl border border-line bg-surface p-1 shadow-pop"
                >
                  <li className="sticky top-0 z-[1] mb-1 bg-surface p-1">
                    <div className="flex items-center gap-2 rounded-lg border border-line bg-surface-2 px-2.5">
                      <Search size={16} className="shrink-0 text-muted" aria-hidden />
                      <input
                        ref={searchRef}
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        onKeyDown={onSearchKeyDown}
                        placeholder={t('common.phone.search', 'Search country or code')}
                        aria-label={t('common.phone.search', 'Search country or code')}
                        className="h-9 min-w-0 flex-1 bg-transparent text-sm text-content placeholder:text-muted/70 outline-none"
                      />
                    </div>
                  </li>

                  <div className="min-h-0 flex-1 overflow-y-auto">
                    {filtered.length === 0 ? (
                      <li className="px-3 py-6 text-center text-sm text-muted">
                        {t('common.phone.no_results', 'No countries found')}
                      </li>
                    ) : (
                      filtered.map((c, index) => {
                        const isSelected = c.code === country;
                        const isActive = index === activeIndex;
                        return (
                          <li
                            key={c.code}
                            ref={(el) => {
                              optionRefs.current[index] = el;
                            }}
                            role="option"
                            aria-selected={isSelected}
                            onClick={() => selectCountry(c.code)}
                            onMouseEnter={() => setActiveIndex(index)}
                            className={cn(
                              'flex cursor-pointer select-none items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm transition-colors',
                              isActive && 'bg-primary/10',
                              isSelected ? 'font-medium text-primary' : 'text-content',
                            )}
                          >
                            <img
                              src={c.flag}
                              alt=""
                              width={20}
                              height={15}
                              loading="lazy"
                              className="h-[15px] w-5 shrink-0 rounded-[3px] border border-line/70 object-cover"
                            />
                            <span className="min-w-0 flex-1 truncate">{c.name}</span>
                            <span className="shrink-0 tabular-nums text-muted">+{c.callingCode}</span>
                            {isSelected && <Check size={16} aria-hidden className="shrink-0 text-primary" />}
                          </li>
                        );
                      })
                    )}
                  </div>
                </motion.ul>
              )}
            </AnimatePresence>,
            document.body,
          )}
      </div>
    );
  },
);

PhoneInput.displayName = 'PhoneInput';
